/**
 * Vendored from `tamoomeh-service/packages/contracts/src/customer-lifecycle.ts`.
 *
 * The Tamoomeh Core API validates a customer request against the same catalog this
 * widget renders, so the two must agree. That copy is canonical; this one is a
 * byte-for-byte copy with only this header added, and `npm run check:contracts`
 * fails when they diverge. Edit Tamoomeh's copy and re-copy, never this file.
 */

/**
 * The customer-facing request lifecycle.
 *
 * The one place where the backend's SCREAMING_SNAKE_CASE request statuses are
 * translated into the short, service-specific Persian vocabulary a customer is
 * allowed to read, and where the notification that each transition produces is
 * described. Core enqueues lifecycle SMS/email from it and the Tamoomeh widget
 * renders the same stages from its vendored copy, so the message a customer
 * receives and the card they see cannot drift apart.
 *
 * ## Why a presentation mapping rather than new backend states
 *
 * Tamoomeh's request state machine is already enough to answer "where is this
 * request?" for every service. Adding a parallel customer state machine
 * would create two sources of truth that could disagree — the exact failure the
 * brief forbids. So this file maps `RequestStatus` onto a customer stage and
 * introduces **no** persisted state.
 *
 * ## Terminal stages
 *
 * `COMPLETED` and `CANCELLED` are real, reachable backend states an operator sets
 * from the admin panel, so the customer must see them named. Leaving them
 * unmapped rendered «در حال به‌روزرسانی وضعیت» forever on a finished request,
 * which reads as a stuck widget rather than an honest neutral state.
 *
 * ## Fail closed
 *
 * A status that has no mapping for a service — an unknown or newly-added enum
 * value — still returns `null`, and the UI renders an honest neutral state
 * instead of guessing. The terminal states are mapped; everything else is not.
 *
 * ## Dependency-free on purpose
 *
 * The widget bundles this file into the browser IIFE, so it must not import zod,
 * `node:*`, or anything else with a runtime cost. The only imports are types.
 */

import type { RequestStatus } from './enums.js';
import type { CustomerServiceSlug } from './customer-services.js';

// ---------------------------------------------------------------------------
// Stages
// ---------------------------------------------------------------------------

/** Every customer-visible stage. */
export const CUSTOMER_LIFECYCLE_STAGES = [
  'awaiting_payment',
  'expert_review',
  'wait_for_contact',
  // Terminal. Both are stored backend statuses an operator sets, so both have a
  // customer-facing name.
  'completed',
  'cancelled',
] as const;

export type CustomerLifecycleStage = (typeof CUSTOMER_LIFECYCLE_STAGES)[number];

export interface CustomerLifecycleStageDefinition {
  id: CustomerLifecycleStage;
  /** The customer-facing title. Never an internal enum name. */
  titleFa: string;
  /** One short sentence directly underneath the title. */
  explanationFa: string;
  /** True when the customer has something to do (today: only paying). */
  actionRequired: boolean;
  /**
   * What the customer should do next, shown only in the expanded detail of an
   * action-required stage. Never invents a URL or a payment provider.
   */
  actionHintFa?: string;
}

/**
 * The stage table.
 *
 * The wording is the brief's, verbatim, because the SMS/email copy and the UI
 * must not drift apart. `explanationFa` is the same sentence in both.
 */
export const CUSTOMER_LIFECYCLE_STAGE_DEFINITIONS: Readonly<
  Record<CustomerLifecycleStage, CustomerLifecycleStageDefinition>
> = {
  awaiting_payment: {
    id: 'awaiting_payment',
    titleFa: 'در انتظار پرداخت',
    explanationFa: 'برای ادامه درخواست، پرداخت خود را تکمیل کنید.',
    actionRequired: true,
    actionHintFa:
      'پرداخت این درخواست هنوز تکمیل نشده است. اگر پرداخت را انجام داده‌اید، چند دقیقه بعد دوباره بررسی کنید؛ در غیر این صورت با پشتیبانی تمومه تماس بگیرید.',
  },
  expert_review: {
    id: 'expert_review',
    titleFa: 'بررسی توسط کارشناس',
    explanationFa: 'کارشناسان تمومه درخواست شما را بررسی می‌کنند.',
    actionRequired: false,
  },
  wait_for_contact: {
    id: 'wait_for_contact',
    titleFa: 'منتظر تماس باشید',
    explanationFa: 'کارشناسان تمومه به‌زودی با شما تماس خواهند گرفت.',
    actionRequired: false,
  },
  completed: {
    id: 'completed',
    titleFa: 'تکمیل شده',
    explanationFa:
      'درخواست شما تکمیل شد. پیام‌ها و فایل‌های ارسالی کارشناس در همین کارت نمایش داده می‌شود.',
    actionRequired: false,
  },
  cancelled: {
    id: 'cancelled',
    titleFa: 'لغو شده',
    explanationFa:
      'این درخواست لغو شده است. برای پیگیری یا ثبت درخواست تازه با پشتیبانی تمومه تماس بگیرید.',
    actionRequired: false,
  },
};

/**
 * The ordered lifecycle of each service.
 *
 * `order` is derived from position, so the progress indicator and the stage
 * table cannot disagree.
 */
const SERVICE_LIFECYCLES: Readonly<Record<CustomerServiceSlug, readonly CustomerLifecycleStage[]>> = {
  vakil: ['awaiting_payment', 'expert_review', 'wait_for_contact', 'completed', 'cancelled'],
  morabi: ['awaiting_payment', 'expert_review', 'wait_for_contact', 'completed', 'cancelled'],
  // Child consultation follows the generic consultation path: pay, an expert
  // reads it, then you are contacted with the answer.
  koodak: ['awaiting_payment', 'expert_review', 'wait_for_contact', 'completed', 'cancelled'],
};

export function customerLifecycleFor(
  service: CustomerServiceSlug,
): readonly CustomerLifecycleStage[] {
  return SERVICE_LIFECYCLES[service] ?? [];
}

/** A stage definition, or `null` when the stage is unknown. */
export function customerStageDefinition(
  stage: string | null | undefined,
): CustomerLifecycleStageDefinition | null {
  if (!stage) return null;
  return (
    (CUSTOMER_LIFECYCLE_STAGE_DEFINITIONS as Record<string, CustomerLifecycleStageDefinition>)[
      stage
    ] ?? null
  );
}

// ---------------------------------------------------------------------------
// The mapping
// ---------------------------------------------------------------------------

const CONSULTATION_MAP: Readonly<Partial<Record<RequestStatus, CustomerLifecycleStage>>> = {
  DRAFT: 'awaiting_payment',
  AWAITING_PAYMENT: 'awaiting_payment',
  PAID: 'expert_review',
  IN_PROGRESS: 'wait_for_contact',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

/**
 * Translate a backend status into the customer's stage.
 *
 * Returns `null` for an unknown service, an unknown status, or a status the
 * service's lifecycle does not cover. Callers must render the neutral
 * fail-closed state rather than the first stage.
 */
export function customerStageFor(
  service: CustomerServiceSlug | string | null | undefined,
  status: RequestStatus | string | null | undefined,
): CustomerLifecycleStage | null {
  if (!service || !status) return null;
  const normalized = String(status).trim().toUpperCase() as RequestStatus;
  if (service === 'vakil' || service === 'morabi' || service === 'koodak') {
    // Child consultation is a consultation service, exactly like vakil/morabi:
    // it pays first, then an expert reads it. It must not be omitted here or a
    // pending-payment child request renders the neutral unknown stage.
    return CONSULTATION_MAP[normalized] ?? null;
  }
  return null;
}

/** The zero-based position of a stage in a service's lifecycle, or -1. */
export function customerStageIndex(
  service: CustomerServiceSlug | string,
  stage: CustomerLifecycleStage | null,
): number {
  if (!stage) return -1;
  const lifecycle = (SERVICE_LIFECYCLES as Record<string, readonly CustomerLifecycleStage[]>)[
    service
  ];
  return lifecycle ? lifecycle.indexOf(stage) : -1;
}

/** The number of stages in a service's lifecycle. */
export function customerStageCount(service: CustomerServiceSlug | string): number {
  return customerLifecycleFor(service as CustomerServiceSlug).length;
}

// ---------------------------------------------------------------------------
// Notification plan
// ---------------------------------------------------------------------------

/** The channels a lifecycle notification uses. Both, always. */
export const CUSTOMER_NOTIFICATION_CHANNELS = ['SMS', 'EMAIL'] as const;
export type CustomerNotificationChannel = (typeof CUSTOMER_NOTIFICATION_CHANNELS)[number];

/** The audience of a lifecycle notification. Only the requester. */
export type CustomerNotificationAudience = 'USER';

/**
 * The stages whose arrival is announced to the customer on a *status*
 * transition.
 *
 * `awaiting_payment` is intentionally absent: its Melipayamak pattern
 * (`CUSTOMER_AWAITING_PAYMENT_SMS` / REQUEST_SUBMITTED body 558740) is the
 * «درخواست … ثبت شد؛ کارشناسان در حال بررسی» message, which must fire only
 * after a payment receipt is successfully persisted — never merely because a
 * request row was created. Core's manual-payment submit path enqueues that
 * template; see `ManualPaymentService.notifySubmitted`.
 *
 * `completed` is not here because each domain service already sends its own
 * completion message (`CASE_COMPLETED_USER`, `REQUEST_COMPLETED_USER`) with the
 * outcome in it; a second generic SMS would announce the same event twice.
 * `cancelled` is not here because a cancellation is almost always the
 * customer's own action. Both keep their templates so they can be enabled here
 * without touching Core.
 */
export const CUSTOMER_NOTIFIED_STAGES: readonly CustomerLifecycleStage[] = [
  'expert_review',
  'wait_for_contact',
];

export interface CustomerNotificationPlanEntry {
  audience: CustomerNotificationAudience;
  channel: CustomerNotificationChannel;
  /** Technical template id; must exist in Tamoomeh's notification renderer. */
  template: string;
}

/**
 * Template ids for the five customer-visible stages.
 *
 * These are the technical ids Core stores in `notifications.template`; the
 * Persian wording lives in the renderer below and in Tamoomeh's label table. They
 * are named after the *customer stage*, never the internal enum, so a renamed
 * backend status cannot rename a customer message.
 */
export const CUSTOMER_LIFECYCLE_TEMPLATES: Readonly<
  Record<CustomerLifecycleStage, { sms: string; email: string }>
> = {
  awaiting_payment: {
    sms: 'CUSTOMER_AWAITING_PAYMENT_SMS',
    email: 'CUSTOMER_AWAITING_PAYMENT_EMAIL',
  },
  expert_review: {
    sms: 'CUSTOMER_EXPERT_REVIEW_SMS',
    email: 'CUSTOMER_EXPERT_REVIEW_EMAIL',
  },
  wait_for_contact: {
    sms: 'CUSTOMER_WAIT_FOR_CONTACT_SMS',
    email: 'CUSTOMER_WAIT_FOR_CONTACT_EMAIL',
  },
  completed: {
    sms: 'CUSTOMER_COMPLETED_SMS',
    email: 'CUSTOMER_COMPLETED_EMAIL',
  },
  cancelled: {
    sms: 'CUSTOMER_CANCELLED_SMS',
    email: 'CUSTOMER_CANCELLED_EMAIL',
  },
};

/** The stage and channel a stored lifecycle template id belongs to, or `null`. */
export function customerLifecycleTemplateInfo(
  template: string | null | undefined,
): { stage: CustomerLifecycleStage; channel: CustomerNotificationChannel } | null {
  if (!template) return null;
  for (const stage of CUSTOMER_LIFECYCLE_STAGES) {
    const templates = CUSTOMER_LIFECYCLE_TEMPLATES[stage];
    if (templates.sms === template) return { stage, channel: 'SMS' };
    if (templates.email === template) return { stage, channel: 'EMAIL' };
  }
  return null;
}

/** Everything a notification needs to name the request without leaking internals. */
export interface CustomerNotificationContext {
  /** The reference number the customer sees, e.g. `REQ-ABCDEFGH`. */
  requestPublicId: string;
  /** The customer-facing service name, e.g. «وکیل». */
  serviceNameFa: string;
  /** The customer's name, when the profile has one. */
  customerNameFa?: string | null;
}

export interface CustomerNotificationContent {
  stage: CustomerLifecycleStage;
  /** The same title the UI shows. */
  titleFa: string;
  /** The same explanation the UI shows. */
  explanationFa: string;
  sms: { template: string; bodyFa: string };
  email: { template: string; subjectFa: string; bodyFa: string };
}

/**
 * Build the SMS and email for one stage.
 *
 * The body reuses the UI's exact title and explanation, adds the reference
 * number and names the service, and closes with the next step. It never names an
 * internal status.
 */
export function customerNotificationFor(
  stage: CustomerLifecycleStage,
  context: CustomerNotificationContext,
): CustomerNotificationContent | null {
  const definition = CUSTOMER_LIFECYCLE_STAGE_DEFINITIONS[stage];
  if (!definition) return null;
  const templates = CUSTOMER_LIFECYCLE_TEMPLATES[stage];
  if (!templates) return null;

  const reference = context.requestPublicId.trim();
  const service = context.serviceNameFa.trim();
  const greeting = context.customerNameFa?.trim() ? `${context.customerNameFa.trim()} عزیز، ` : '';
  const nextStep = definition.actionRequired
    ? definition.actionHintFa ?? definition.explanationFa
    : 'نیازی به اقدام از طرف شما نیست؛ در صورت تغییر وضعیت، دوباره اطلاع می‌دهیم.';

  const smsBodyFa = [
    `${greeting}وضعیت درخواست ${service} شما: ${definition.titleFa}`,
    definition.explanationFa,
    `کد پیگیری: ${reference}`,
  ].join('\n');

  const emailSubjectFa = `${definition.titleFa} — درخواست ${service} (${reference})`;
  const emailBodyFa = [
    `${greeting}وضعیت درخواست ${service} شما به‌روزرسانی شد.`,
    '',
    `وضعیت: ${definition.titleFa}`,
    definition.explanationFa,
    '',
    `کد پیگیری: ${reference}`,
    `اقدام بعدی: ${nextStep}`,
    '',
    'تمومه — رسانه و خدمات حقوقی، ورزشی و کودک',
  ].join('\n');

  return {
    stage,
    titleFa: definition.titleFa,
    explanationFa: definition.explanationFa,
    sms: { template: templates.sms, bodyFa: smsBodyFa },
    email: { template: templates.email, subjectFa: emailSubjectFa, bodyFa: emailBodyFa },
  };
}

/** Normalise a status for comparison; `null` means "no previous status". */
function normalizeStatus(status: RequestStatus | string | null | undefined): string | null {
  if (status === null || status === undefined) return null;
  const trimmed = String(status).trim().toUpperCase();
  return trimmed ? trimmed : null;
}

export interface CustomerLifecycleTransitionInput {
  service: CustomerServiceSlug | string;
  fromStatus: RequestStatus | string | null | undefined;
  toStatus: RequestStatus | string | null | undefined;
}

/**
 * Resolve the stage a transition lands on, or `null` when nothing should be sent.
 *
 * A message is produced only when the stored status changed
 * (`DRAFT → AWAITING_PAYMENT`). A repeated write of the same status is a no-op
 * and produces nothing, so a re-read or a retried job cannot send a second
 * message — on top of the idempotency key, which is the real guarantee.
 */
export function customerStageTransition(
  input: CustomerLifecycleTransitionInput,
): CustomerLifecycleStage | null {
  const next = customerStageFor(input.service, input.toStatus);
  if (!next) return null;
  if (normalizeStatus(input.fromStatus) === normalizeStatus(input.toStatus)) return null;
  return next;
}

/**
 * The notifications a status transition produces.
 *
 * Returns `[]` — nothing at all — unless the transition actually moved the
 * request into a customer-visible stage listed in `CUSTOMER_NOTIFIED_STAGES`. A
 * page view, a no-op write, or a status the service's lifecycle does not cover
 * produces no message.
 */
export function customerNotificationPlanForTransition(
  input: CustomerLifecycleTransitionInput,
): CustomerNotificationPlanEntry[] {
  const stage = customerStageTransition(input);
  if (!stage || !CUSTOMER_NOTIFIED_STAGES.includes(stage)) return [];
  const templates = CUSTOMER_LIFECYCLE_TEMPLATES[stage];
  if (!templates) return [];
  return [
    { audience: 'USER', channel: 'SMS', template: templates.sms },
    { audience: 'USER', channel: 'EMAIL', template: templates.email },
  ];
}

/**
 * The idempotency key for one notification.
 *
 * Deterministic in the request, the resulting stage and the channel, so a retry
 * of the same business operation — or a repeated delivery of the same domain
 * event — resolves to the same key and Core's existing queue drops the
 * duplicate. It deliberately does **not** include a timestamp or a random value:
 * either would make every retry a new message.
 */
export function customerNotificationIdempotencyKey(input: {
  requestPublicId: string;
  stage: CustomerLifecycleStage;
  channel: CustomerNotificationChannel;
  audience?: CustomerNotificationAudience;
}): string {
  const audience = input.audience ?? 'USER';
  return [
    'customer-lifecycle',
    input.requestPublicId.trim(),
    input.stage,
    audience,
    input.channel,
  ].join(':');
}

/** The two keys a transition produces, in plan order. */
export function customerNotificationKeysForTransition(
  input: CustomerLifecycleTransitionInput & { requestPublicId: string },
): {
  stage: CustomerLifecycleStage;
  channel: CustomerNotificationChannel;
  template: string;
  idempotencyKey: string;
}[] {
  const stage = customerStageTransition(input);
  if (!stage) return [];
  return customerNotificationPlanForTransition(input).map((entry) => ({
    stage,
    channel: entry.channel,
    template: entry.template,
    idempotencyKey: customerNotificationIdempotencyKey({
      requestPublicId: input.requestPublicId,
      stage,
      channel: entry.channel,
      audience: entry.audience,
    }),
  }));
}

// ---------------------------------------------------------------------------
// Request progress timeline (five display steps)
// ---------------------------------------------------------------------------

/**
 * The five customer-visible progress steps required by the product brief.
 *
 * This is a pure presentation mapping over persisted backend state — it
 * introduces no new stored status. Order is fixed:
 *
 *   1. `created` — «ایجاد درخواست» (initial step)
 *   2. `awaiting_payment` — «در انتظار پرداخت»
 *   3. `awaiting_review` — «در انتظار تأیید پرداخت» (receipt awaiting admin review)
 *   4. `approved` — «تأیید پرداخت» (admin approved the receipt/payment)
 *   5. `wait_for_contact` — «منتظر تماس باشید» (admin completed the request),
 *       or «منتظر فایل برنامه باشید» for a sports request whose persisted
 *       `wantsCall` flag is false (the customer did not ask for a coach call).
 */
export const REQUEST_PROGRESS_STEPS = [
  'created',
  'awaiting_payment',
  'awaiting_review',
  'approved',
  'wait_for_contact',
] as const;

export type RequestProgressStepId = (typeof REQUEST_PROGRESS_STEPS)[number];

export interface RequestProgressStep {
  id: RequestProgressStepId;
  titleFa: string;
  state: 'done' | 'current' | 'todo';
}

export const REQUEST_PROGRESS_TITLES: Readonly<Record<RequestProgressStepId, string>> = {
  created: 'ایجاد درخواست',
  awaiting_payment: 'در انتظار پرداخت',
  awaiting_review: 'در انتظار تأیید پرداخت',
  approved: 'تأیید پرداخت',
  wait_for_contact: 'منتظر تماس باشید',
};

/**
 * The final-step label for a sports request without a coach-call request.
 *
 * Shown instead of «منتظر تماس باشید» only when the request is a sports
 * consultation (`morabi` on the customer surface, `mamad` in Core) and the
 * canonical persisted `wantsCall` flag is exactly `false`.
 */
export const SPORT_WAIT_FOR_PROGRAM_TITLE_FA = 'منتظر فایل برنامه باشید';

export interface RequestProgressInput {
  requestStatus: string | null | undefined;
  paymentStatus?: string | null | undefined;
  /** Latest manual receipt status (`PENDING`/`APPROVED`/`REJECTED`), when known. */
  receiptStatus?: string | null | undefined;
  /** Server-authoritative receipt block reason, when known. */
  receiptBlockedReason?: string | null | undefined;
  /**
   * The customer section (`morabi`) or Core service (`mamad`) slug, when known.
   * Only a sports consultation may relabel the final step; every other service
   * always keeps «منتظر تماس باشید».
   */
  customerService?: string | null | undefined;
  /**
   * The canonical persisted coach-call flag (`metadata.wantsCall`). Only an
   * explicit `false` relabels the final step; `true`, `null` and `undefined`
   * (unknown or legacy rows) preserve the existing label — never a guess.
   */
  wantsCall?: boolean | null | undefined;
}

function upper(value: string | null | undefined): string {
  return String(value ?? '').trim().toUpperCase();
}

/** Whether the slug identifies the sports consultation service. */
function isSportServiceForProgress(value: string | null | undefined): boolean {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'morabi' || normalized === 'mamad';
}

/**
 * The final-step title for this request.
 *
 * Sports consultations whose persisted `wantsCall` flag is exactly `false`
 * wait for the programme file, not for a call; every other service — and any
 * request without a known explicit opt-out — keeps «منتظر تماس باشید».
 */
function waitForContactTitle(input: RequestProgressInput): string {
  if (isSportServiceForProgress(input.customerService) && input.wantsCall === false) {
    return SPORT_WAIT_FOR_PROGRAM_TITLE_FA;
  }
  return REQUEST_PROGRESS_TITLES.wait_for_contact;
}

/**
 * Map persisted request/payment state onto the five display steps.
 *
 * Rules (explicit and testable):
 * - `created` is done as soon as anything beyond creation happened, otherwise current.
 * - `awaiting_payment` is done only after a receipt was submitted (manual
 *   `PENDING`/`APPROVED`, payment `PENDING`/`PAID`, `PENDING_REVIEW`, or the
 *   request left `DRAFT`/`AWAITING_PAYMENT`).
 * - `awaiting_review` is current while a receipt awaits admin review, done
 *   once payment is approved.
 * - `approved` is done only after admin approval (payment `PAID` or request
 *   `PAID`/`IN_PROGRESS`/`COMPLETED`); never before.
 * - `wait_for_contact` is done only after admin request completion
 *   (`COMPLETED`); never merely because payment was approved.
 * - Rejected receipts/payments, failures and cancellations never mark future
 *   steps done; a rejected receipt returns the customer to `awaiting_payment`.
 */
export function requestProgressFor(input: RequestProgressInput): RequestProgressStep[] {
  const requestStatus = upper(input.requestStatus);
  const paymentStatus = upper(input.paymentStatus);
  const receiptStatus = upper(input.receiptStatus);
  const blockedReason = upper(input.receiptBlockedReason);

  if (requestStatus === 'CANCELLED') {
    return [
      { id: 'created', titleFa: REQUEST_PROGRESS_TITLES.created, state: 'done' },
      { id: 'awaiting_payment', titleFa: REQUEST_PROGRESS_TITLES.awaiting_payment, state: 'todo' },
      { id: 'awaiting_review', titleFa: REQUEST_PROGRESS_TITLES.awaiting_review, state: 'todo' },
      { id: 'approved', titleFa: REQUEST_PROGRESS_TITLES.approved, state: 'todo' },
      { id: 'wait_for_contact', titleFa: waitForContactTitle(input), state: 'todo' },
    ];
  }

  const receiptPending =
    receiptStatus === 'PENDING' || blockedReason === 'PENDING_REVIEW' || paymentStatus === 'PENDING';
  const receiptRejected =
    receiptStatus === 'REJECTED' || paymentStatus === 'UNPAID' || paymentStatus === 'FAILED';
  const paymentApproved =
    paymentStatus === 'PAID' ||
    receiptStatus === 'APPROVED' ||
    requestStatus === 'PAID' ||
    requestStatus === 'IN_PROGRESS' ||
    requestStatus === 'COMPLETED';
  const completed = requestStatus === 'COMPLETED';
  const inContactStage = requestStatus === 'IN_PROGRESS' || completed;
  const receiptSubmitted =
    receiptPending || paymentApproved || blockedReason === 'ALREADY_PAID' || paymentStatus === 'REDIRECTED';

  // A rejected receipt with no live pending review sends the customer back to
  // paying: nothing beyond creation is done.
  if (receiptRejected && !receiptPending && !paymentApproved) {
    return [
      { id: 'created', titleFa: REQUEST_PROGRESS_TITLES.created, state: 'done' },
      { id: 'awaiting_payment', titleFa: REQUEST_PROGRESS_TITLES.awaiting_payment, state: 'current' },
      { id: 'awaiting_review', titleFa: REQUEST_PROGRESS_TITLES.awaiting_review, state: 'todo' },
      { id: 'approved', titleFa: REQUEST_PROGRESS_TITLES.approved, state: 'todo' },
      { id: 'wait_for_contact', titleFa: waitForContactTitle(input), state: 'todo' },
    ];
  }

  if (completed) {
    return REQUEST_PROGRESS_STEPS.map((id) => ({
      id,
      titleFa: id === 'wait_for_contact' ? waitForContactTitle(input) : REQUEST_PROGRESS_TITLES[id],
      state: 'done' as const,
    }));
  }

  if (paymentApproved) {
    return [
      { id: 'created', titleFa: REQUEST_PROGRESS_TITLES.created, state: 'done' },
      { id: 'awaiting_payment', titleFa: REQUEST_PROGRESS_TITLES.awaiting_payment, state: 'done' },
      { id: 'awaiting_review', titleFa: REQUEST_PROGRESS_TITLES.awaiting_review, state: 'done' },
      { id: 'approved', titleFa: REQUEST_PROGRESS_TITLES.approved, state: 'done' },
      { id: 'wait_for_contact', titleFa: waitForContactTitle(input), state: 'current' },
    ];
  }

  if (receiptSubmitted || inContactStage) {
    // Receipt awaiting admin review (or an edge where the request moved without
    // a recorded approval — still never mark approval done).
    const reviewDone = false;
    void reviewDone;
    return [
      { id: 'created', titleFa: REQUEST_PROGRESS_TITLES.created, state: 'done' },
      { id: 'awaiting_payment', titleFa: REQUEST_PROGRESS_TITLES.awaiting_payment, state: 'done' },
      { id: 'awaiting_review', titleFa: REQUEST_PROGRESS_TITLES.awaiting_review, state: 'current' },
      { id: 'approved', titleFa: REQUEST_PROGRESS_TITLES.approved, state: 'todo' },
      { id: 'wait_for_contact', titleFa: waitForContactTitle(input), state: 'todo' },
    ];
  }

  return [
    { id: 'created', titleFa: REQUEST_PROGRESS_TITLES.created, state: 'current' },
    { id: 'awaiting_payment', titleFa: REQUEST_PROGRESS_TITLES.awaiting_payment, state: 'todo' },
    { id: 'awaiting_review', titleFa: REQUEST_PROGRESS_TITLES.awaiting_review, state: 'todo' },
    { id: 'approved', titleFa: REQUEST_PROGRESS_TITLES.approved, state: 'todo' },
    { id: 'wait_for_contact', titleFa: waitForContactTitle(input), state: 'todo' },
  ];
}
